import {AuthenticationService, BaseApiService} from '.';
import { adminAuthenticationService } from '.';
import { config } from 'config';
import { IContest } from 'interfaces/IContest';
import axios from 'axios';
import { ILeague, ITeam } from 'interfaces/league';
import { ILeagueSeason } from 'interfaces/league/ILeagueSeason';
import { IStockHistory } from 'interfaces/IStockHistory';
import { IPredictionAdmin } from 'interfaces/IPrediction';
import {
  AnalysisOutputParams,
  DateEx,
  IQuoteFull,
  IStockSymbolAdmin,
  PredictionTypeEnum,
  StockStatsIntervalEnum as Interval,
  IAutoTradeSetup,
  IAutoTrade,
  IBKRSide,
  AutoTradePositionTypeEnum,
  AutoTradeStatusEnum,
  AutoTradeManagementTypeEnum,
} from 'predictagram-lib';
import { IAward } from 'interfaces/IAward';
import { ISignalAlert } from 'components/admin/analysis/strategy-profit/ISignalAlert';


class AdminApiService<T> extends BaseApiService<T> { }

class AdminContestApiService extends AdminApiService<IContest> {
  async addUsers(id: number, payload: any): Promise<boolean> {
    const url = `${this.apiBaseUrl}/contest/add-users/${id}`;
    try {
      const response = await axios.post<any>(url, payload, await this.authenticationService.getAuthHeader());
      if (response.data.error) {
        throw Error(response.data.error.message);
      }
      return response.data as boolean
    } catch (error: any) {
      throw Error((error as Error).message);
    }
  }
}

class AdminStockHistoryApiService extends BaseApiService<IStockHistory>{
  async getAll8PM(searchOptions: {
    startTime: number,
    endTime: number,
    symbolName?: string,
  }): Promise<IStockHistory[]> {
    try {
      const response: any = await axios.post<IStockHistory[]>(this.allUrl, {filters:searchOptions}, await this.authenticationService.getAuthHeader());
      if (response.data.error) {
        throw Error(response.data.error.message);
      }
      return response.data.data as IStockHistory[];
    } catch (error: any) {
      throw Error(error.message);
    }
  }
}

class AdminLeagueSeasonApiService extends BaseApiService<ILeagueSeason> { }
class AdminLeagueTeamApiService extends BaseApiService<ITeam> { }
class AdminLeagueApiService extends BaseApiService<ILeague> { }

export interface IPredictionAdminSearchOptions {
  predictionType?: PredictionTypeEnum,
  predictionTypes?: PredictionTypeEnum[],
  symbolNames?: string[],
  startTime: number,
  endTime: number,
  limit?: number,
  userId?: number,
  usenameLike?: string,
  emailLike?: string,
}

class AdminPredictionApiService extends BaseApiService<IPredictionAdmin> {
  async search(searchOptions: IPredictionAdminSearchOptions): Promise<IPredictionAdmin[]> {
    try {
      // @TODO: convert to array in UI
      const type = parseInt(searchOptions.predictionType as any, 10);
      if (type) {
        searchOptions.predictionTypes = [type];
      }
      const response = await axios.post<IPredictionAdmin[] | any>(this.allUrl, { filters: searchOptions }, await this.authenticationService.getAuthHeader());
      if (response.data.error) {
        throw Error(response.data.error.message);
      }
      return response.data.data as IPredictionAdmin[];
    } catch (error: any) {
      throw Error(error.message);
    }
  }

  async cumulativeData(searchOptions: {symbolNames?:string[],predictionTypes?:any[],startTime?:number,endTime?:number,startTimePredMade?:number, endTimePredMade?:number}): Promise<IPredictionAdmin[]> {
    try {
      const url = `${this.apiBaseUrl}/prediction/stats/cumulative`;
      const response = await axios.post<IPredictionAdmin[] | any>(url, { filters: searchOptions }, await this.authenticationService.getAuthHeader());
      if (response.data.error) {
        throw Error(response.data.error.message);
      }
      return response.data.data as IPredictionAdmin[];
    } catch (error: any) {
      throw Error(error.message);
    }
  }

  async getBarsData(tickerSymbol: string, startDateTime: DateEx, endDateTime: DateEx, cacheOk=false, interval: Interval= Interval.MIN_1): Promise<ReadonlyArray<IQuoteFull>> {

    const apiUrl = `${this.apiBaseUrl}/stock/stats/${interval}`;
    try {
      const response = await axios.post(apiUrl, {filters: {
          symbolName: tickerSymbol,
          startTime: startDateTime.getTimeSec(),
          endTime: endDateTime.getTimeSec(),
        }}, await this.authenticationService.getAuthHeader());
      const d = response.data.data;
      return d;
    } catch (error: any) {
      throw Error((error as Error).message);
    }

  }
}

class AdminAwardApiService extends BaseApiService<IAward> {
  async awardUser(awardId: number, userId: number): Promise<any>{
    const url = `${this.apiBaseUrl}/award/award-user`;
    try {
      const response = await axios.post<any>(url, {awardId, userId}, await this.authenticationService.getAuthHeader());
      if (response.data.error) {
        throw Error(response.data.error.message);
      }
      return response.data as boolean
    } catch (error: any) {
      throw Error((error as Error).message);
    }

  }
}

class AdminSignalAlertApiService extends BaseApiService<ISignalAlert> {
  async getAll(withAuth: boolean = true, usePost: boolean = false) {
    const all = await super.getAll(withAuth, true);
    return all.sort((a, b) => b.id - a.id )
  }
}



class AdminAutoTradeSetupService extends BaseApiService<IAutoTradeSetup> {}

export interface IAutoTradeSearchFilter {
  autoTradeId?: number,
  startTime?: number,
  endTime?: number,
  ibkrSide?: IBKRSide,
  positionType?: AutoTradePositionTypeEnum,
  autoTradeStatusIds?: AutoTradeStatusEnum[],
  autoTradeManagementTypeId?: AutoTradeManagementTypeEnum
}

export interface IAutoTradePrice {
  lastPrice: number,
  askPrice: number,
  bidPrice: number,
  bestTradePrice: number,
}

class AdminAutoTradesService extends BaseApiService<IAutoTrade> {
  async getFilteredData(filters: Partial<IAutoTradeSearchFilter>): Promise<IAutoTrade[]> {
    try {
      const response: any = await axios.post<IAutoTrade[]>(this.allUrl, {filters}, await this.authenticationService.getAuthHeader());
      if (response.data.error) {
        throw Error(response.data.error.message);
      }
      return response.data.data as IAutoTrade[];
    } catch (error: any) {
      throw Error(error.message);
    }
  }

  async getLiveSymbolPrices(symbolId: number): Promise<IAutoTradePrice> {
    const url = `${this.apiBaseUrl}/auto-trade/live-symbol-prices/${symbolId}`;
    try {
      const response: any = await axios.post<IAutoTradePrice>(url, {}, await this.authenticationService.getAuthHeader());
      if (response.data.error) {
        throw Error(response.data.error.message);
      }
      return response.data as IAutoTradePrice;

    } catch (error: any) {
      throw Error(error.message);
    }    
  }

}


export interface IAutoTradeLivePrice {
  lastPrice: number,
  askPrice: number,
  bidPrice: number,
}

class AdminApiServiceCommon  {

  constructor(
    protected apiBaseUrl:string,
    protected authenticationService: AuthenticationService) {
  }

  async queryData(req:()=>Promise<any>) {
    try {
      const response = await req();
      if (response.data.error) {
        throw Error(response.data.error.message);
      }
      return response.data as any;
    } catch (error: any) {
      throw Error((error as Error).message);
    }
}

  async analysisStrategyProfit(data:any): Promise<AnalysisOutputParams>{
    const url = `${this.apiBaseUrl}/analysis/strategy-profit`;
    return this.queryData(async()=>axios.post<any>(url, data, await this.authenticationService.getAuthHeader()));
  }

  async analysisStrategyProfitDaily(data:any): Promise<any>{
    const url = `${this.apiBaseUrl}/analysis/strategy-profit-daily`;
    return this.queryData(async()=>axios.post<any>(url, data, await this.authenticationService.getAuthHeader()));
  }

  async analysisStrategyProfitCombined(data:any): Promise<any>{
    const url = `${this.apiBaseUrl}/analysis/strategy-profit-combined`;
    return this.queryData(async()=>axios.post<any>(url, data, await this.authenticationService.getAuthHeader()));
  }

  async createSignalAlert(data:any) {
    const url = `${this.apiBaseUrl}/analysis/signal/alert`;
    return this.queryData(async()=>axios.post<any>(url, data, await this.authenticationService.getAuthHeader()));
  }

  async updateSignalAlert(data:any, id: number) {
    const url = `${this.apiBaseUrl}/analysis/signal/alert/${id}`;
    return this.queryData(async()=>axios.put<any>(url, data, await this.authenticationService.getAuthHeader()));
  }

  async signalAlerts(data:any) {
    const url = `${this.apiBaseUrl}/analysis/signal/alerts`;
    return this.queryData(async()=>axios.post<any>(url, data, await this.authenticationService.getAuthHeader()));
  }

  async deleteSignalAlert(id:number) {
    const url = `${this.apiBaseUrl}/analysis/signal/alert/${id}`;
    return this.queryData(async()=>axios.delete<any>(url, await this.authenticationService.getAuthHeader()));
  }

  async getSymbols():Promise<IStockSymbolAdmin[]> {
    const url = `${this.apiBaseUrl}/stock/symbols`;
    return this.queryData(async()=>{
      return (await axios.post<any>(url, await this.authenticationService.getAuthHeader())).data
    });
  }

  async createTradeSetup(data: any) {
    const url = `${this.apiBaseUrl}/auto-trade/setup`;
    return this.queryData(async()=>axios.post<any>(url, data, await this.authenticationService.getAuthHeader()));
  }

  async updateTradeSetup(data:any, id: number) {
    const url = `${this.apiBaseUrl}/auto-trade/setup/${id}`;
    return this.queryData(async()=>axios.put<any>(url, data, await this.authenticationService.getAuthHeader()));
  }

  // async deleteTradeSetup(data:any, id: number) {
  //   const url = `${this.apiBaseUrl}/auto-trade/${id}`;
  //   return this.queryData(async()=>axios.delete<any>(url, await this.authenticationService.getAuthHeader()));
  // }

  async listTradeSetups() {
    const url = `${this.apiBaseUrl}/auto-trade/trades`;
    const data = {}; 
    return this.queryData(async()=>axios.post<any>(url, data, await this.authenticationService.getAuthHeader()));
  }

  async closeTrade(id:number): Promise<boolean> {
    const url = `${this.apiBaseUrl}/auto-trade/trade/close/${id}`;
    const result = await this.queryData(async()=>axios.post(url, {}, await this.authenticationService.getAuthHeader()));
    return !!result?.success;
  }

  async addTrade(id:number) {
    const url = `${this.apiBaseUrl}/auto-trade/trade/add-like/${id}`;
    return this.queryData(async()=>axios.post(url, {}, await this.authenticationService.getAuthHeader()));
  }

  async updateTrade(id:number, data:Partial<IAutoTrade>) {
    const url = `${this.apiBaseUrl}/auto-trade/trade/close/${id}`;
    return this.queryData(async()=>axios.put(url, data, await this.authenticationService.getAuthHeader()));
  }

  async getTradeLivePrices(id:number): Promise<IAutoTradeLivePrice> {
    const url = `${this.apiBaseUrl}/auto-trade/trade/live-prices/${id}`;
    const result = await this.queryData(async()=>axios.post(url, {}, await this.authenticationService.getAuthHeader()));
    return result;
  }

}

const apiBaseUrl: string = `${config.apiDomain}${config.apiBasePath}/admin`;
const adminContestApiService: AdminContestApiService = new AdminContestApiService(apiBaseUrl, '/contest', '/contests', adminAuthenticationService);
const adminLeagueApiService: AdminLeagueApiService = new AdminLeagueApiService(apiBaseUrl, '/league', '/leagues', adminAuthenticationService);
const adminLeagueTeamApiService: AdminLeagueTeamApiService = new AdminLeagueTeamApiService(apiBaseUrl, '/league/team', '/league/teams', adminAuthenticationService);
const adminLeagueSeasonApiService: AdminLeagueSeasonApiService = new AdminLeagueSeasonApiService(apiBaseUrl, '/league/season', '/league/seasons', adminAuthenticationService);
const adminStockHistoryApiService: AdminStockHistoryApiService = new AdminStockHistoryApiService(apiBaseUrl, '/stock/history', '/stock/histories', adminAuthenticationService);
const adminPredictionApiService: AdminPredictionApiService = new AdminPredictionApiService(apiBaseUrl, '/prediction', '/predictions', adminAuthenticationService);
const adminAwardApiService: AdminAwardApiService = new AdminAwardApiService(apiBaseUrl, '/award', '/awards', adminAuthenticationService);
const adminApiServiceCommon: AdminApiServiceCommon = new AdminApiServiceCommon(apiBaseUrl, adminAuthenticationService);
const adminSignalAlertApiService: AdminSignalAlertApiService = new AdminSignalAlertApiService(apiBaseUrl, '/analysis/signal/alert', '/analysis/signal/alerts', adminAuthenticationService);
const adminAutoTradeSetupService: AdminAutoTradeSetupService = new AdminAutoTradeSetupService(apiBaseUrl, '/auto-trade/setup', '/auto-trade/setups', adminAuthenticationService);
const adminAutoTradesService: AdminAutoTradesService = new AdminAutoTradesService(apiBaseUrl, '/auto-trade/trade', '/auto-trade/trades', adminAuthenticationService);

export {
  AdminApiService,
  AdminPredictionApiService,
  adminContestApiService,
  adminLeagueApiService,
  adminLeagueTeamApiService,
  adminLeagueSeasonApiService,
  adminStockHistoryApiService,
  adminPredictionApiService,
  adminAwardApiService,
  adminSignalAlertApiService,
  adminApiServiceCommon,
  adminAutoTradeSetupService,
  adminAutoTradesService
}
